"use client";

import { useEffect, useRef, useState } from "react";
import {
  ChecklistIcon,
  LogoutIcon,
  MunicipalityIcon,
  ReceiptIcon,
  ShoppingIcon,
} from "@aidnas/design-system";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { signOut } from "next-auth/react";

import { useCart } from "@/context/CartContext";

type User = {
  name: string;
  email: string;
  role: "employee" | "admin" | "muni";
  image?: string | null;
  municipality?: string | null;
  isCxManager?: boolean;
} | null;

type NavItem = { href: string; label: string; dividerBefore?: boolean };

const NAV: Record<string, NavItem[]> = {
  admin: [
    { href: "/shop", label: "Catalog" },
    { href: "/account/orders", label: "My orders" },
    { href: "/admin", label: "Admin", dividerBefore: true },
  ],
  employee: [
    { href: "/shop", label: "Catalog" },
    { href: "/account/orders", label: "My orders" },
  ],
  muni: [
    { href: "/muni/shop", label: "Catalog" },
    { href: "/muni/orders", label: "My orders" },
    { href: "/muni/profile", label: "My profile" },
  ],
};

const CART_HREF: Record<string, string> = {
  admin: "/checkout",
  employee: "/checkout",
  muni: "/muni/checkout",
};

const ORDERS_HREF: Record<string, string> = {
  admin: "/account/orders",
  employee: "/account/orders",
  muni: "/muni/orders",
};

const AIDN_SUB: Record<string, string> = {
  admin: "Aidn · Admin",
  employee: "Aidn · Employee",
};

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function TopBar({ user }: { user: User }) {
  const pathname = usePathname();
  const router = useRouter();
  const { count } = useCart();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  if (!user) return null;

  const baseNav = NAV[user.role] ?? NAV.employee;
  const nav = (() => {
    if (!user.isCxManager) return baseNav;
    const insertAt = baseNav.findIndex((i) => i.dividerBefore);
    const muniItem: NavItem = {
      href: "/admin/my-munis",
      label: "My muni orders",
      dividerBefore: true,
    };
    if (insertAt === -1) return [...baseNav, muniItem];
    return [...baseNav.slice(0, insertAt), muniItem, ...baseNav.slice(insertAt)];
  })();
  const cartHref = CART_HREF[user.role];
  const ordersHref = ORDERS_HREF[user.role];
  const displayName = user.name || user.email;
  const displaySub =
    user.role === "muni" ? (user.municipality ?? "Municipality") : (AIDN_SUB[user.role] ?? "");

  return (
    <header className="mx-topbar">
      <div className="mx-topbar-left">
        <Link
          aria-label="Home"
          className="mx-brand-btn"
          href={nav[0].href}
          style={{ textDecoration: "none" }}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img alt="" height={28} src="/aidn-logomark.svg" width={28} />
          <div className="mx-wordmark">
            <span>aidn shop</span>
          </div>
        </Link>

        <nav className="mx-topnav">
          {nav.map(({ dividerBefore, href, label }) => {
            const active = pathname === href || (href !== "/" && pathname.startsWith(href + "/"));
            return (
              <span key={href} className="mx-topnav-entry">
                {dividerBefore && (
                  <span aria-hidden="true" className="mx-topnav-divider">
                    |
                  </span>
                )}
                <Link className={"mx-topnav-item" + (active ? " is-active" : "")} href={href}>
                  {label}
                </Link>
              </span>
            );
          })}
        </nav>
      </div>

      <div className="mx-topbar-right">
        {cartHref && (
          <button aria-label="Cart" className="mx-cart-btn" onClick={() => router.push(cartHref)}>
            <ShoppingIcon size={16} />
            <span>Cart</span>
            {count > 0 && <span className="mx-cart-badge">{count}</span>}
          </button>
        )}

        <div ref={dropdownRef} className="mx-user">
          <button
            aria-expanded={dropdownOpen}
            aria-label="User menu"
            className="mx-avatar-btn"
            onClick={() => setDropdownOpen((v) => !v)}
          >
            <div className="mx-user-meta">
              <div className="mx-user-name">{displayName}</div>
              <div className="mx-user-sub">{displaySub}</div>
            </div>
            {user.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt={displayName}
                className="mx-avatar-img"
                referrerPolicy="no-referrer"
                src={user.image}
              />
            ) : (
              <div className="ads-avatar">{initials(displayName)}</div>
            )}
          </button>

          {dropdownOpen && (
            <div className="mx-user-dropdown">
              <Link
                className="mx-dropdown-header"
                href={user.role === "muni" ? "/muni/profile" : "/account/profile"}
                onClick={() => setDropdownOpen(false)}
                style={{ textDecoration: "none", display: "block" }}
              >
                <div className="mx-user-name">{displayName}</div>
                <div className="mx-user-sub">{displaySub}</div>
              </Link>
              <div className="mx-dropdown-divider" />
              <Link
                className="mx-dropdown-item"
                href={ordersHref}
                onClick={() => setDropdownOpen(false)}
              >
                <ReceiptIcon size={16} />
                My orders
              </Link>
              {user.isCxManager && (
                <>
                  <div className="mx-dropdown-divider" />
                  <Link
                    className="mx-dropdown-item"
                    href="/admin/my-munis"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <MunicipalityIcon size={16} />
                    My muni orders
                  </Link>
                </>
              )}
              {user.role === "admin" && (
                <>
                  <div className="mx-dropdown-divider" />
                  <Link
                    className="mx-dropdown-item"
                    href="/admin"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <ChecklistIcon size={16} />
                    Admin
                  </Link>
                </>
              )}
              <div className="mx-dropdown-divider" />
              <button
                className="mx-dropdown-item"
                onClick={() => signOut({ callbackUrl: "/login" })}
              >
                <LogoutIcon size={16} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
